import logger from '@/modules/Logger';
import mainStore from '@/store/index';

class GetDeviceAccess {
  /**
   * Request access to a specific media device
   * @param {object} deviceContraints
   */
  async getUserMedia(deviceContraints) {
    const device = Object.keys(deviceContraints)?.[0];
    try {
      await navigator.mediaDevices.getUserMedia(deviceContraints);
      mainStore.dispatch(`devices/setMediaDeviceError`, { device });
    } catch (error) {
      this.handleDeviceError(device, error);
    }
  }

  handleDeviceError(device, error) {
    const deviceLabel = device === 'video' ? 'camera' : 'microphone';
    
    // Ignore OverconstrainedError; the provider will activate another device
    if (error.name === 'OverconstrainedError') {
      mainStore.dispatch(`devices/setMediaDeviceError`, { device });
      return;
    }

    if (error.code === 'PERMISSION_DENIED' || error.name === 'NotAllowedError') {
      logger.error(`User denied permission to access the ${deviceLabel}`, { operation: 'device:get-media' });
      mainStore.dispatch(`devices/setMediaDeviceError`, { name: 'NotAllowedError', device });
      return;
    }

    const errorMessages = {
      NotReadableError: `The ${deviceLabel} is already in use by another application`,
      NotFoundError: `No ${deviceLabel} detected`
    };

    logger.error(errorMessages[error.name] || error.message, { operation: 'device:get-media' })
    mainStore.dispatch(`devices/setMediaDeviceError`, { name: error.name || error.code, device });
  }

  /**
   * Request permissions for both audio and video
   * @param {string} videoId
   * @param {string} audioId
   */
  async initiateDeviceRequest(videoId, audioId) {
    // dont use Promise.all, queue it properly 
    // calling getUserMedia simultaneously will make the 2nd call hang in firefox
    await this.getCameraDevice(videoId);
    await this.getMicDevice(audioId);
  }

  /**
   * Request permissions for camera
   * @param {string} videoId
   */
  async getCameraDevice(videoId) {
    const videoContraints = { deviceId: { exact: videoId }};
    await this.getUserMedia({ video: videoId ? videoContraints : true });
  }

  /**
   * Request permissions for mic
   * @param {string} audioId
   */
  async getMicDevice(audioId) {
    const audioContraints = { deviceId: { exact: audioId }};
    await this.getUserMedia({ audio: audioId ? audioContraints: true });
  }
}

const getDeviceAccessInstance = new GetDeviceAccess();
export { GetDeviceAccess, getDeviceAccessInstance };
